import { useCallback, useEffect, useReducer, useRef, useState, type RefObject } from 'react';

export type LocationCheckResult = 'ready' | 'waiting' | 'closer' | 'interrupted';
export interface LocationCheckBudget {
  lastAt: number;
  active: boolean;
}

const CHECK_INTERVAL_MS = 15_000;
const CHECK_TIMEOUT_MS = 20_000;
const budgetListeners = new WeakMap<LocationCheckBudget, Set<() => void>>();

function notifyBudget(budget: LocationCheckBudget) {
  budgetListeners.get(budget)?.forEach((notify) => notify());
}

function reserveBudget(budget: RefObject<LocationCheckBudget>, now: number) {
  if (budget.current.active || now - budget.current.lastAt < CHECK_INTERVAL_MS) return false;
  budget.current.active = true;
  budget.current.lastAt = now;
  notifyBudget(budget.current);
  return true;
}

function releaseBudget(budget: RefObject<LocationCheckBudget>) {
  budget.current.active = false;
  notifyBudget(budget.current);
}

/** Presentation only. verify must honor cancellation and settle; it never grants eligibility. */
export function useLocationCheck({
  key,
  enabled,
  verify,
  budget,
}: {
  key?: string;
  enabled: boolean;
  verify: (signal: AbortSignal) => Promise<LocationCheckResult>;
  budget: RefObject<LocationCheckBudget>;
}) {
  const [result, setResult] = useState<{
    key?: string;
    pending: boolean;
    outcome?: LocationCheckResult;
  }>({ pending: false });
  const [delayed, setDelayed] = useState(false);
  const [foregroundVisible, setForegroundVisible] = useState(document.visibilityState !== 'hidden');
  const [clock, setClock] = useState(Date.now);
  const [, refreshBudget] = useReducer((revision: number) => revision + 1, 0);
  const controller = useRef<AbortController | null>(null);
  const deadlineRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const generation = useRef(0);
  const scope = useRef<{ key?: string; available: boolean } | undefined>(undefined);
  const interrupted = useRef(false);
  const verifyRef = useRef(verify);
  const available = enabled && foregroundVisible && Boolean(key);

  // GPS presentation can update the callback frequently. That must not restart
  // the automatic schedule or invalidate an otherwise current request.
  useEffect(() => {
    verifyRef.current = verify;
  }, [verify]);

  useEffect(() => {
    const shared = budget.current;
    const notify = () => {
      setClock(Date.now());
      refreshBudget();
    };
    let listeners = budgetListeners.get(shared);
    if (!listeners) {
      listeners = new Set();
      budgetListeners.set(shared, listeners);
    }
    listeners.add(notify);
    return () => {
      listeners.delete(notify);
      if (listeners.size === 0) budgetListeners.delete(shared);
    };
  }, [budget]);

  useEffect(() => {
    function visibilityChanged() {
      const visible = document.visibilityState !== 'hidden';
      if (!visible) {
        generation.current += 1;
        controller.current?.abort();
        clearTimeout(deadlineRef.current);
      }
      setForegroundVisible(visible);
    }
    document.addEventListener('visibilitychange', visibilityChanged);
    return () => document.removeEventListener('visibilitychange', visibilityChanged);
  }, []);

  const runCheck = useCallback(
    async (automatic: boolean) => {
      const now = Date.now();
      if (
        !available ||
        !key ||
        !scope.current?.available ||
        scope.current.key !== key ||
        document.visibilityState === 'hidden'
      )
        return;
      setClock(now);
      if ((automatic && interrupted.current) || !reserveBudget(budget, now)) return;
      const request = new AbortController();
      const epoch = generation.current;
      controller.current = request;
      interrupted.current = false;
      setResult({ key, pending: true });
      const isCurrent = () =>
        !request.signal.aborted &&
        generation.current === epoch &&
        document.visibilityState !== 'hidden';
      const deadline = setTimeout(() => {
        if (isCurrent()) {
          interrupted.current = true;
          setResult({ key, pending: false, outcome: 'interrupted' });
          setDelayed(true);
        }
        request.abort();
      }, CHECK_TIMEOUT_MS);
      deadlineRef.current = deadline;
      try {
        const outcome = await verifyRef.current(request.signal);
        if (isCurrent()) {
          interrupted.current = outcome === 'interrupted';
          setResult({ key, pending: false, outcome });
        }
      } catch {
        if (isCurrent()) {
          interrupted.current = true;
          setResult({ key, pending: false, outcome: 'interrupted' });
        }
      } finally {
        clearTimeout(deadline);
        // Even after timeout/unmount, retain the shared lock until the actual
        // verification settles. Promise.race would permit overlapping proof calls.
        if (controller.current === request) {
          controller.current = null;
          deadlineRef.current = undefined;
          releaseBudget(budget);
        }
      }
    },
    [available, budget, key],
  );
  const check = useCallback(() => runCheck(false), [runCheck]);

  useEffect(() => {
    let cancelled = false;
    scope.current = { key, available };
    queueMicrotask(() => {
      if (cancelled) return;
      setResult({ key, pending: false });
      setDelayed(false);
      interrupted.current = false;
      if (available) void runCheck(true);
    });
    const interval = available
      ? setInterval(() => void runCheck(true), CHECK_INTERVAL_MS)
      : undefined;
    const delay = available ? setTimeout(() => setDelayed(true), CHECK_TIMEOUT_MS) : undefined;
    return () => {
      cancelled = true;
      scope.current = undefined;
      generation.current += 1;
      controller.current?.abort();
      clearTimeout(deadlineRef.current);
      clearInterval(interval);
      clearTimeout(delay);
    };
  }, [available, runCheck, key]);

  const pending = available && result.key === key && result.pending;
  return {
    pending,
    outcome: available && result.key === key ? result.outcome : undefined,
    delayed: available && delayed,
    canRetry:
      available &&
      !pending &&
      !budget.current.active &&
      clock >= budget.current.lastAt + CHECK_INTERVAL_MS,
    check,
  };
}
